/**
 * First-party funnel ingest — POSTs to this project's `funnelHttp` (Firestore-backed).
 *
 * Everything here is best-effort by design: a funnel event must never break, block,
 * or slow a purchase. Every failure path swallows silently.
 *
 * ⚠️ Funnel sessions are what the abandonment sweep runs on
 * (firebase/functions/scheduled/funnelAbandonment.js). Sending `checkout_started`
 * makes a session eligible to be marked abandoned, and attaching an email via
 * `postFunnelLink` makes it eligible for a reminder EMAIL. So this module is the
 * on-switch for abandoned-cart mail — see docs/NMI_TESTING.md before enabling it in
 * an environment whose FUNNEL_ABANDON_* config is still at defaults.
 *
 * Client dedupes (type + scopeKey) within 2s, which absorbs React StrictMode's
 * double-invoked effects.
 */
import {
  clearFunnelSessionCookie,
  getOrCreateFunnelSessionId,
  touchFunnelSession,
  getStableContext,
} from './funnelSession.js'

const DEFAULT_FUNNEL_HTTP_BASE =
  'https://us-central1-healberry-dashboard.cloudfunctions.net/funnelHttp'

const FUNNEL_HTTP_BASE = (
  import.meta.env.VITE_FUNNEL_HTTP_BASE || DEFAULT_FUNNEL_HTTP_BASE
).replace(/\/$/, '')

/** Marks the once-per-session context payload as already sent. */
const CTX_SENT_KEY = 'hb_funnel_ctx_sent_v1'
const DEDUPE_MS = 2000
const FLUSH_MS = 400

/** @type {{ type: string, payload: Record<string, unknown>, path: string, ts: number, context?: ReturnType<typeof getStableContext> }[]} */
let queue = []
let flushTimer = 0
/** @type {Map<string, number>} */
const recentDedupe = new Map()

/** Cross-origin POST — credentials omitted, so no cookies travel cross-site. */
function postFunnelJson(url, bodyString, keepalive) {
  return fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: bodyString,
    mode: 'cors',
    credentials: 'omit',
    ...(keepalive ? { keepalive: true } : {}),
  })
}

function dedupeHit(type, scopeKey) {
  const key = `${type}::${scopeKey}`
  const now = Date.now()
  const prev = recentDedupe.get(key)
  if (prev != null && now - prev < DEDUPE_MS) return true
  recentDedupe.set(key, now)
  for (const [k, t] of recentDedupe) {
    if (now - t > DEDUPE_MS * 4) recentDedupe.delete(k)
  }
  return false
}

function scheduleFlush() {
  if (typeof window === 'undefined') return
  if (flushTimer) window.clearTimeout(flushTimer)
  flushTimer = window.setTimeout(() => {
    flushTimer = 0
    void flushQueue()
  }, FLUSH_MS)
}

function eventBody(sessionId, item) {
  return JSON.stringify({
    sessionId,
    type: item.type,
    ts: Date.now(),
    path: item.path,
    payload: item.payload && typeof item.payload === 'object' ? item.payload : {},
    ...(item.context ? { context: item.context } : {}),
  })
}

async function flushQueue() {
  if (typeof window === 'undefined' || queue.length === 0) return
  if (!FUNNEL_HTTP_BASE) {
    queue = []
    return
  }
  const batch = queue
  queue = []

  const sessionId = getOrCreateFunnelSessionId()
  if (!sessionId) {
    // Put them back rather than dropping — the cookie may exist by the next flush.
    queue = batch.concat(queue)
    return
  }

  const url = `${FUNNEL_HTTP_BASE}/event`
  for (const item of batch) {
    try {
      const res = await postFunnelJson(url, eventBody(sessionId, item), false)
      // A rejected sessionId would otherwise fail every event for the life of the
      // cookie. Clearing it lets the next call mint a valid one.
      if (res.status === 400) {
        let errCode = ''
        try {
          const json = await res.json()
          errCode = typeof json?.error === 'string' ? json.error : ''
        } catch {
          /* ignore */
        }
        if (errCode.includes('sessionId')) clearFunnelSessionCookie()
      }
    } catch {
      /* ignore — analytics must never surface to the shopper */
    }
  }
}

if (typeof window !== 'undefined') {
  // `pagehide` rather than `unload`: it fires on iOS Safari and on back/forward
  // cache navigations, where `unload` does not. `keepalive` lets the request
  // outlive the document.
  window.addEventListener('pagehide', () => {
    if (!FUNNEL_HTTP_BASE || queue.length === 0) return
    const pending = queue
    queue = []
    const sessionId = getOrCreateFunnelSessionId()
    if (!sessionId) return
    const url = `${FUNNEL_HTTP_BASE}/event`
    for (const item of pending) {
      void postFunnelJson(url, eventBody(sessionId, item), true).catch(() => {})
    }
  })
}

/**
 * @param {{ type: string, payload?: Record<string, unknown>, path?: string, scopeKey?: string }} input
 */
export function trackFunnelEvent(input) {
  const type = typeof input.type === 'string' ? input.type : ''
  if (!type || typeof window === 'undefined') return
  if (!FUNNEL_HTTP_BASE) return

  const scopeKey = input.scopeKey || type
  if (dedupeHit(type, scopeKey)) return

  const sessionId = getOrCreateFunnelSessionId()
  if (!sessionId) return

  touchFunnelSession()

  const path =
    typeof input.path === 'string' && input.path
      ? input.path
      : window.location.pathname + (window.location.search || '')

  const payload = input.payload && typeof input.payload === 'object' ? { ...input.payload } : {}

  /** @type {ReturnType<typeof getStableContext> | undefined} */
  let context
  try {
    if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(CTX_SENT_KEY) !== '1') {
      sessionStorage.setItem(CTX_SENT_KEY, '1')
      context = getStableContext()
    }
  } catch {
    /* private-mode sessionStorage can throw — context is optional */
  }

  queue.push({ type, payload, path, ts: Date.now(), ...(context ? { context } : {}) })
  scheduleFlush()
}

/**
 * Associate the checkout session (and optionally the shopper's email) with the
 * funnel doc.
 *
 * ⚠️ The email is what makes an abandoned session eligible for a reminder EMAIL.
 * Nothing else in this module transmits it.
 *
 * @param {{ checkoutSessionId?: string, email?: string | null }} input
 */
export function postFunnelLink(input) {
  if (typeof window === 'undefined') return
  if (!FUNNEL_HTTP_BASE) return
  const sessionId = getOrCreateFunnelSessionId()
  const checkoutSessionId =
    typeof input.checkoutSessionId === 'string' ? input.checkoutSessionId.trim() : ''
  const emailTrim =
    typeof input.email === 'string' && input.email.trim() ? input.email.trim().toLowerCase() : ''
  if (!sessionId || (!checkoutSessionId && !emailTrim)) return

  touchFunnelSession()

  const body = JSON.stringify({
    sessionId,
    ...(checkoutSessionId ? { checkoutSessionId } : {}),
    ...(emailTrim ? { email: emailTrim } : {}),
  })
  void postFunnelJson(`${FUNNEL_HTTP_BASE}/link`, body, true).catch(() => {})
}

/** The current funnel session id, for passing to the checkout API. */
export function currentFunnelSessionId() {
  if (typeof window === 'undefined') return ''
  return getOrCreateFunnelSessionId()
}
